import type { Column } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Filter } from "lucide-react";
import React, { useState } from "react";
import { cn } from "src/common/utils";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface FilterableHeaderProps<TData> {
  column: Column<TData, unknown>;
  title: string;
  filterOptions: FilterOption[];
  selectedValues: Set<string>;
  onFilterChange: (values: Set<string>) => void;
  multiSelect?: boolean;
}

export function FilterableHeader<TData>({
  column,
  title,
  filterOptions,
  selectedValues,
  onFilterChange,
  multiSelect = true,
}: FilterableHeaderProps<TData>) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const canSort = column.getCanSort();
  const sortDirection = column.getIsSorted();
  const hasActiveFilter = selectedValues.size > 0;

  const handleSort = () => {
    if (canSort) {
      column.toggleSorting(sortDirection === "asc");
    }
  };

  const toggleOption = (value: string) => {
    const newValues = new Set(selectedValues);
    if (multiSelect) {
      if (newValues.has(value)) {
        newValues.delete(value);
      } else {
        newValues.add(value);
      }
    } else {
      // Single select - clear and set
      newValues.clear();
      if (!selectedValues.has(value)) {
        newValues.add(value);
      }
    }
    onFilterChange(newValues);
  };

  const clearFilter = () => {
    onFilterChange(new Set());
    setIsFilterOpen(false);
  };

  const SortIcon = sortDirection === "asc"
    ? ArrowUp
    : sortDirection === "desc"
      ? ArrowDown
      : ArrowUpDown;

  return (
    <div className="flex items-center gap-0.5">
      {/* Sort button */}
      <button
        type="button"
        onClick={handleSort}
        disabled={!canSort}
        className={cn(
          "-ml-2 flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium",
          "transition-colors",
          canSort && "hover:bg-card hover:text-foreground"
        )}
      >
        <span>{title}</span>
        {canSort && (
          <SortIcon
            className={cn(
              "h-4 w-4",
              sortDirection ? "opacity-100" : "opacity-50"
            )}
          />
        )}
      </button>

      {/* Filter button */}
      <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded transition-colors",
              "hover:bg-card",
              hasActiveFilter ? "text-foreground" : "text-muted-foreground"
            )}
          >
            <Filter className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-48 p-0"
        >
          <div className="py-1">
            {/* All option - clears filter */}
            <button
              type="button"
              className={cn(
                "w-full px-3 py-1.5 text-left hover:bg-accent",
                "flex items-center gap-2 text-sm transition-colors",
                !hasActiveFilter ? "text-blue-500 dark:text-blue-400" : "text-foreground"
              )}
              onClick={clearFilter}
            >
              <Check
                className={cn(
                  "h-4 w-4 flex-shrink-0",
                  !hasActiveFilter ? "opacity-100" : "opacity-0"
                )}
              />
              All
            </button>

            <div className="h-px bg-accent my-1" />

            {/* Filter options */}
            <div className="max-h-48 overflow-y-auto">
              {filterOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={cn(
                    "w-full px-3 py-1.5 text-left hover:bg-accent",
                    "flex items-center gap-2 text-sm transition-colors",
                    selectedValues.has(option.value)
                      ? "text-blue-500 dark:text-blue-400"
                      : "text-foreground"
                  )}
                  onClick={() => toggleOption(option.value)}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      selectedValues.has(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.icon && (
                    <span className="flex-shrink-0">{option.icon}</span>
                  )}
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
